// src/components/analytics/MarketingTracker.tsx
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackMarketingEvent } from '../../lib/marketingEvents';

function cleanText(value: string | null | undefined) {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 90);
}

function findTrackableElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest('a,button,[data-track-event],[data-forfeo-track]') as HTMLElement | null;
}

export default function MarketingTracker() {
  const location = useLocation();
  const lastPageKey = useRef('');

  useEffect(() => {
    const key = `${location.pathname}${location.search}`;
    if (lastPageKey.current === key) return;
    lastPageKey.current = key;

    const timer = window.setTimeout(() => {
      trackMarketingEvent({
        event_type: 'page_view',
        event_name: 'Page vue',
        label: document.title || location.pathname,
        path: key,
        source: 'route_change',
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const element = findTrackableElement(event.target);
      if (!element) return;

      const href = element instanceof HTMLAnchorElement ? element.href : '';
      const label =
        element.dataset.trackLabel ||
        element.getAttribute('aria-label') ||
        cleanText(element.textContent) ||
        (href ? 'Lien' : 'Bouton');

      let eventType = element.dataset.trackEvent || 'click';
      if (href) {
        try {
          const url = new URL(href);
          const isForfeoCom = url.hostname === 'forfeo.com' || url.hostname.endsWith('.forfeo.com');
          const isCurrentHost = url.hostname === window.location.hostname;
          if (isForfeoCom && !isCurrentHost) eventType = 'outbound_forfeo_com';
          else if (!isCurrentHost) eventType = 'outbound_click';
          else eventType = 'internal_click';
        } catch (_) {}
      }

      trackMarketingEvent({
        event_type: eventType,
        event_name: label,
        label,
        target_url: href,
        source: 'global_click_listener',
        metadata: {
          tag: element.tagName.toLowerCase(),
          id: element.id || undefined,
          classes: element.className?.toString?.().slice(0, 180),
        },
      });
    };

    window.addEventListener('click', onClick, { capture: true, passive: true });
    return () => window.removeEventListener('click', onClick, { capture: true } as any);
  }, []);

  return null;
}
